import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { caseStudies } from "../data/caseStudies.js";
import { NAV_LINKS } from "../data/navLinks.js";
import Eyebrow from "./Eyebrow.jsx";
import "./Footer.css";

gsap.registerPlugin(ScrollTrigger);

const EMAIL = "hello@version3.ai";
// FormSubmit relays the POST to our inbox: no server of our own. First ever submission sends an activation mail to EMAIL.
const FORM_ENDPOINT = `https://formsubmit.co/ajax/${EMAIL}`;

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/version3ai/",
    icon: "M5.40161 2.5C4.48974 2.5 3.75 3.23852 3.75 4.15039C3.75 5.06227 4.48914 5.81787 5.40039 5.81787C6.31164 5.81787 7.052 5.06227 7.052 4.15039C7.052 3.23914 6.31349 2.5 5.40161 2.5ZM13.4595 6.875C12.0726 6.875 11.2791 7.60028 10.8984 8.32153H10.8582V7.06909H8.125V16.25H10.9729V11.7053C10.9729 10.5078 11.0633 9.35059 12.5452 9.35059C14.0058 9.35059 14.0271 10.716 14.0271 11.781V16.25H16.8713H16.875V11.2073C16.875 8.73978 16.3445 6.875 13.4595 6.875ZM3.97705 7.06909V16.25H6.82739V7.06909H3.97705Z",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/share/14tyyXQzmxR/",
    icon: "M12.6 4.43368H14.1666V1.78368C13.4081 1.70481 12.6459 1.66586 11.8833 1.66702C9.61663 1.66702 8.06663 3.05035 8.06663 5.58368V7.76702H5.5083V10.7337H8.06663V18.3337H11.1333V10.7337H13.6833L14.0666 7.76702H11.1333V5.87535C11.1333 5.00035 11.3666 4.43368 12.6 4.43368Z",
  },
];

const HEADING_WORDS = [
  { text: "Have" },
  { text: "a" },
  { text: "Problem" },
  { text: "Worth" },
  { text: "Solving?" },
];

function ArrowUpRight() {
  return (
    <svg aria-hidden="true" fill="none" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 16 16" width="100%" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.69922 11.2998L11.2989 4.70014" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.2992 8.47156V4.70032" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.52832 4.7002H11.2996" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// liquid blobs that melt the light CTA panel into the dark one (goo filter in the SVG)
const BLOBS = [
  { cx: 120, cy: 300, r: 120 },
  { cx: 330, cy: 250, r: 150 },
  { cx: 560, cy: 310, r: 110 },
  { cx: 760, cy: 230, r: 160 },
  { cx: 980, cy: 300, r: 130 },
  { cx: 1180, cy: 240, r: 150 },
  { cx: 1370, cy: 300, r: 120 },
];

export default function Footer() {
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  const rootRef = useRef(null);
  const successRef = useRef(null);
  // case study and careers pages close with their own CTA, so the footer drops panels 1 + 2 there
  const { pathname } = useLocation();
  const underOnly = pathname.startsWith("/work/") || pathname.startsWith("/careers") || pathname === "/privacy-policy" || pathname === "/terms-of-service";

  // optimistic: confirm straight away, clear the inputs on success, bring everything back (values kept) if the send fails
  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setFailed(false);
    setSubmitted(true);
    if (data.get("_honey")) return form.reset(); // bot filled the hidden field: fake success, send nothing
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === "false" || json.success === false) throw new Error(json.message || `HTTP ${res.status}`);
      form.reset();
    } catch (err) {
      console.error("Contact form send failed:", err);
      setSubmitted(false);
      setFailed(true);
    }
  }

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const q = (sel) => root.querySelector(sel);
    const qa = (sel) => Array.from(root.querySelectorAll(sel));

    const cleanups = [];

    const ctx = gsap.context(() => {
      const cta = q(".ft-cta");
      if (!cta) return;

      // headline: words rise out of a mask, then the rest of the panel follows
      gsap
        .timeline({ scrollTrigger: { trigger: cta, start: "top 65%", once: true } })
        .from(".ft-eyebrow", { opacity: 0, y: 16, duration: 0.6, ease: "power3.out" })
        .from(".ft-word-in", { yPercent: 115, rotate: 4, duration: 1, ease: "expo.out", stagger: 0.08 }, "-=0.3")
        .from(".ft-cta-actions", { opacity: 0, y: 24, duration: 0.8, ease: "power3.out" }, "-=0.6");

      // liquid: each blob drifts on its own, the whole mass rises as the panel scrolls past
      // (the goo filter re-renders on every move, so they only run while the panel is on screen)
      const blobTweens = qa(".ft-blob").map((blob) =>
        gsap.to(blob, {
          x: gsap.utils.random(-70, 70),
          y: gsap.utils.random(-40, 30),
          duration: gsap.utils.random(3, 5.5),
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          paused: true,
        })
      );
      ScrollTrigger.create({
        trigger: cta,
        start: "top bottom",
        end: "bottom top",
        onToggle: (self) => blobTweens.forEach((t) => t.paused(!self.isActive)),
      });
      gsap.fromTo(
        ".ft-liquid-mass",
        { y: 170 },
        { y: 0, ease: "none", scrollTrigger: { trigger: cta, start: "top 30%", end: "bottom 70%", scrub: 0.6 } }
      );
      gsap.fromTo(
        ".ft-heading",
        { y: 0 },
        { y: -60, ease: "none", scrollTrigger: { trigger: cta, start: "top top", end: "bottom top", scrub: true } }
      );

      // contact panel
      gsap
        .timeline({ scrollTrigger: { trigger: ".ft-contact", start: "top 70%", once: true } })
        .from(".ft-contact-copy > *", { opacity: 0, y: 36, duration: 0.9, ease: "power3.out", stagger: 0.1 })
        .from(".ft-card", { opacity: 0, y: 60, duration: 1, ease: "power3.out" }, "-=0.8");

      // big marquee: base drift, pushed faster (and flipped) by scroll velocity
      const track = q(".ft-marquee-track");
      const group = track?.firstElementChild;
      if (track && group) {
        let x = 0;
        let dir = -1;
        let smooth = 0;
        let visible = false;
        let velocity = 0;

        ScrollTrigger.create({ trigger: ".ft-marquee", start: "top bottom", end: "bottom top", onToggle: (self) => { visible = self.isActive; } });
        ScrollTrigger.create({ start: 0, end: "max", onUpdate: (self) => { velocity = self.getVelocity(); } });

        const tick = (_t, dtMs) => {
          if (!visible) return;
          const dt = Math.min(dtMs, 50) / 1000;
          smooth += (velocity - smooth) * 0.1;
          if (Math.abs(smooth) > 40) dir = smooth > 0 ? -1 : 1;
          const speed = 70 + Math.abs(smooth) * 0.18;
          const w = group.offsetWidth;
          x += dir * speed * dt;
          if (x <= -w) x += w;
          if (x > 0) x -= w;
          track.style.transform = `translate3d(${x}px,0,0) skewX(${gsap.utils.clamp(-8, 8, -smooth / 350)}deg)`;
        };
        gsap.ticker.add(tick);
        cleanups.push(() => gsap.ticker.remove(tick));
      }
    }, root);

    // under-panel: revealed as the curtain slides off; links stagger in, wordmark rises.
    // without a curtain, the panel itself drives the same reveal.
    const underCtx = gsap.context(() => {
      const curtain = q(".ft-curtain");
      const under = q(".ft-under");
      if (!under) return;

      gsap.from(".ft-under-col, .ft-under-bottom", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.08,
        scrollTrigger: curtain
          ? { trigger: curtain, start: "bottom 80%", once: true }
          : { trigger: under, start: "top 80%", once: true },
      });
      gsap.fromTo(
        ".ft-wordmark",
        { yPercent: 42 },
        {
          yPercent: 0,
          ease: "none",
          scrollTrigger: {
            trigger: curtain || under,
            start: curtain ? "bottom bottom" : "top bottom",
            end: () => `+=${under.offsetHeight}`,
            scrub: 0.5,
            invalidateOnRefresh: true,
          },
        }
      );
    }, root);

    return () => {
      ctx.revert();
      underCtx.revert();
      cleanups.forEach((fn) => fn());
    };
  }, [underOnly]);

  return (
    <footer className={`footer${underOnly ? " footer--under-only" : ""}`} ref={rootRef}>
      {!underOnly && (
      <div className="ft-curtain">
        {/* ---------- 1. light CTA panel + liquid edge ---------- */}
        <section className="ft-cta">
          <div className="ft-grain" aria-hidden="true"></div>
          <div className="ft-cta-inner">
            <Eyebrow section="contact" className="ft-eyebrow" />
            <h2 className="ft-heading" aria-label="Have a Problem Worth Solving?">
              {HEADING_WORDS.map((word) => (
                <span aria-hidden="true" className="ft-word" key={word.text}>
                  <span className="ft-word-in">{word.text}</span>{" "}
                </span>
              ))}
            </h2>
            <p className="ft-lede">
              Tell us what you're trying to build, automate, or improve. We'll figure out the technology with you.
            </p>
            <div className="ft-cta-actions">
              <a className="ft-submit ft-submit--dark" href="#CTA-Form">
                <span className="ft-submit-bg"></span>
                <span className="ft-submit-text">Tell us about it</span>
                <span className="ft-submit-icon"><ArrowUpRight /></span>
              </a>
            </div>
          </div>

          {/* tells the navbar to switch to light text while it sits over the ink liquid */}
          <div aria-hidden="true" className="ft-nav-dark" data-nav-theme="dark"></div>

          <svg aria-hidden="true" className="ft-liquid" preserveAspectRatio="xMidYMax slice" viewBox="0 0 1440 460">
            <defs>
              <filter colorInterpolationFilters="sRGB" height="140%" id="ft-goo" width="120%" x="-10%" y="-20%">
                <feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="26" />
                <feColorMatrix in="blur" result="goo" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 26 -11" />
                <feGaussianBlur in="goo" stdDeviation="7" />
              </filter>
            </defs>
            <g className="ft-liquid-mass" filter="url(#ft-goo)">
              <rect fill="#0e0e10" height="200" width="1560" x="-60" y="320" />
              {BLOBS.map((b) => (
                <circle className="ft-blob" cx={b.cx} cy={b.cy} fill="#0e0e10" key={b.cx} r={b.r} />
              ))}
            </g>
          </svg>
        </section>

        {/* ---------- 2. dark contact panel: email, form, marquee ---------- */}
        <section className="ft-contact" data-nav-theme="dark">
          <div className="ft-contact-grid">
            <div className="ft-contact-copy">
              <div className="ft-label">Email</div>
              <a className="ft-email" href={`mailto:${EMAIL}`}>{EMAIL}</a>
              <div className="ft-contact-buttons">
                <a className="ft-btn ft-btn--solid" href={`mailto:${EMAIL}`}>
                  <span>Email us</span>
                  <span className="ft-btn-icon"><ArrowUpRight /></span>
                </a>
                <Link className="ft-btn" to="/#work">
                  <span>See our work</span>
                </Link>
              </div>
            </div>

            <div className="ft-card" id="CTA-Form">
              <div className="cta-form form-wrap">
                <form hidden={submitted} aria-label="Email Form" className="ft-form" id="email-form" name="email-form" onSubmit={handleSubmit}>
                  <input type="hidden" name="_subject" value="New project request from the Version3 portfolio" />
                  <input type="hidden" name="_template" value="table" />
                  <input type="hidden" name="_captcha" value="false" />
                  <input aria-hidden="true" autoComplete="off" name="_honey" style={{ display: "none" }} tabIndex="-1" type="text" />
                  <label className="ft-sr-only" htmlFor="Name">Your name</label>
                  <input className="ft-input form-input" autoComplete="name" id="Name" maxLength="256" name="Name" placeholder="Your name*" required type="text" />
                  <label className="ft-sr-only" htmlFor="Company">Your company name</label>
                  <input className="ft-input form-input" autoComplete="organization" id="Company" maxLength="256" name="Company" placeholder="Your company name*" required type="text" />
                  <label className="ft-sr-only" htmlFor="Email">Your business email</label>
                  <input className="ft-input form-input" autoComplete="email" id="Email" maxLength="256" name="Email" placeholder="Your business email*" required type="email" />
                  <label className="ft-sr-only" htmlFor="Share-project-details">Share your project details</label>
                  <textarea className="ft-input is-text-area form-input" id="Share-project-details" maxLength="5000" name="Share-project-details" placeholder="Message" required></textarea>
                  <button className="ft-submit" type="submit">
                    <span className="ft-submit-bg"></span>
                    <span className="ft-submit-text">Send Your Request!</span>
                    <span className="ft-submit-icon"><ArrowUpRight /></span>
                  </button>
                </form>
                <div aria-label="Email Form success" className={`ft-success form-done${submitted ? " is-visible" : ""}`} ref={successRef} role="region" tabIndex="-1">
                  <div className="ft-success-title">Thank you!</div>
                  <p className="ft-success-text">We got your request and will reply from {EMAIL} soon.</p>
                </div>
                <div aria-label="Email Form failure" className="ft-error form-fail" hidden={!failed} role="alert" tabIndex="-1">
                  Something went wrong sending that. Please try again, or email us at {EMAIL}.
                </div>
              </div>
            </div>
          </div>

          {/* only the first item is exposed, so the link's name is its visible text: "Start a project" */}
          <a className="ft-marquee" href={`mailto:${EMAIL}`}>
            <div className="ft-marquee-track">
              {[0, 1].map((g) => (
                <div aria-hidden={g === 1 ? "true" : undefined} className="ft-marquee-group" key={g}>
                  {[0, 1, 2, 3].map((i) => (
                    <span aria-hidden={g === 0 && i > 0 ? "true" : undefined} className="ft-marquee-item" key={i}>
                      Start a project
                      <span className="ft-marquee-arrow"><ArrowUpRight /></span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </a>
        </section>
      </div>
      )}

      {/* ---------- 3. light grainy panel, revealed from under the dark one ---------- */}
      <div className="ft-under">
        <div className="ft-grain" aria-hidden="true"></div>
        <div className="ft-glow" aria-hidden="true"></div>
        <div className="ft-under-inner">
          <div className="ft-under-cols">
            <nav aria-label="Footer" className="ft-under-col">
              <div className="ft-under-title">Navigate</div>
              {NAV_LINKS.map((link) => (
                <Link className="ft-ulink" key={link.href} to={link.href}>{link.label}</Link>
              ))}
            </nav>
            <div className="ft-under-col">
              <div className="ft-under-title">Case studies</div>
              {caseStudies.map((study) => (
                <Link className="ft-ulink" key={study.slug} to={`/work/${study.slug}`}>{study.title}</Link>
              ))}
            </div>
            <div className="ft-under-col">
              <div className="ft-under-title">Socials</div>
              {SOCIAL_LINKS.map((link) => (
                <a className="ft-ulink ft-ulink--ext" href={link.href} key={link.label} rel="noopener noreferrer" target="_blank">
                  {link.label}
                  <span className="ft-ulink-arrow"><ArrowUpRight /></span>
                </a>
              ))}
            </div>
          </div>

          <div className="ft-under-bottom">
            <div className="ft-legal">
              <span>&copy; Version3 {new Date().getFullYear()}. All rights reserved.</span>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms-of-service">Terms of Service</Link>
            </div>
            <button className="ft-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} type="button">
              Back to top
              <span className="ft-top-btn-icon"><ArrowUpRight /></span>
            </button>
            <div className="ft-social-icons">
              {SOCIAL_LINKS.map((link) => (
                <a aria-label={link.label} className="ft-social-icon" href={link.href} key={link.label} rel="noopener noreferrer" target="_blank">
                  <svg aria-hidden="true" fill="none" height="100%" viewBox="0 0 20 20" width="100%" xmlns="http://www.w3.org/2000/svg">
                    <path d={link.icon} fill="currentColor" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="ft-wordmark-wrap">
          <img alt="Version3" className="ft-wordmark" height="734" loading="lazy" src="/assets/images/logofooter_v3.webp" width="2141" />
        </div>
      </div>
    </footer>
  );
}
